import { useQuery } from '@tanstack/react-query';
import { myPermissions, type MyPermissions } from '@/api/client';
import { canSeePage, type PageKey } from '@/lib/permissions';
import { useAuth } from '@/contexts/AuthContext';

/**
 * The signed-in user's permissions at their active site, and the one function
 * that decides whether a module is visible.
 *
 * THE BRIDGE, AND WHY IT EXISTS.
 * Only some modules have been migrated to the operation registry. If the nav
 * simply asked RBAC about everything, every unmigrated module would vanish —
 * the registry does not mention them, which is not the same as "denied".
 *
 * So each module is decided by whichever source is actually authoritative
 * for it:
 *
 *   module IS registered   -> the RBAC grid decides, full stop. This is what
 *                             makes "what you tick is what you see" true.
 *   module NOT registered  -> the hard-coded table decides, exactly as today.
 *
 * `registered_modules` comes from the server rather than a list kept here, so
 * the boundary moves on its own as domains are migrated and the two halves
 * cannot drift apart.
 *
 * While the request is in flight, or if it fails, every module falls back to
 * the hard-coded table — a nav that briefly shows the old answer is far better
 * than one that flashes empty and strands the user.
 */
/**
 * PageKey -> RBAC module.
 *
 * Several pages belong to one module: /studies, the create wizard, the
 * workspace and onboarding are all the `studies` module, so one grid cell
 * governs them together.
 *
 * A PageKey with no entry here has no RBAC module and is always decided by the
 * hard-coded table — which is the right answer for things like the RBAC screen
 * itself, deliberately kept unconfigurable.
 */
export const PAGE_TO_MODULE: Partial<Record<PageKey, string>> = {
  dashboard: 'dashboard',
  studies: 'studies',
  'studies.create': 'studies',
  'studies.workspace': 'studies',
  'studies.onboarding': 'studies',
  subjects: 'subjects',
  visits: 'visits',
  forms: 'forms',
  esource: 'esource',
  queries: 'queries',
  binder: 'binder',
  doa: 'delegations',
  approvals: 'approvals',
  delegations: 'delegations',
  search: 'search',
  'signature-queue': 'signature_queue',
  'document-library': 'document_library',
  team: 'team',
  training: 'training',
  finance: 'finance',
  compliance: 'compliance',
  'platform-admin': 'platform_admin',
  blinding: 'blinding',
  'build-master-data': 'master_data',
  sponsors: 'sponsors',
};

/**
 * ACTION pages — PageKeys that mean "do this", not "look at this module".
 *
 * These resolve to ONE operation and are gated on holding exactly it. A page
 * absent from here is a module page and uses PAGE_TO_MODULE: visible when the
 * role holds any verb, which is the client's left-panel rule.
 *
 * The distinction is not cosmetic. /studies/new sits in the (studies, create)
 * cell alongside studies.ie_checklist.create and
 * studies.schedule.visit.create; a role holding only those two would otherwise
 * reach a wizard it cannot submit.
 */
export const PAGE_TO_OPERATION: Partial<Record<PageKey, string>> = {
  'studies.create': 'studies.create',
  // The onboarding wizard answers to the same operation the backend now
  // checks (requireStudySetupAccess's `operation`), so the route and the API
  // agree. On the module union it was reachable by anyone holding ANY studies
  // verb — a monitor could open the wizard and be refused by every action in
  // it. Gating on the operation removes that dead end.
  'studies.onboarding': 'studies.onboarding',
  // Monitor Assignments follows RBAC › Permissions · enforced (Harish, 13 Oct
  // 2026): visible exactly to whoever the grid lets manage assignments.
  'monitor-assignments': 'compliance.monitor_assignments',
};

export function useMyPermissions() {
  const { user } = useAuth();

  const q = useQuery({
    // Keyed on the active site: switching site must refetch, because the
    // grid is per site.
    queryKey: ['my-permissions', user?.id, user?.activeSiteId],
    queryFn: myPermissions.get,
    enabled: !!user,
    staleTime: 60_000,
  });

  const data: MyPermissions | undefined = q.data;
  const registered = new Set(data?.registered_modules ?? []);

  /** Verbs the user holds on a module at this site. Empty when none. */
  const verbsFor = (module: string): string[] => data?.modules?.[module] ?? [];

  /** Does the user hold this specific verb? Only meaningful for registered modules. */
  const can = (module: string, verb: string): boolean => verbsFor(module).includes(verb);

  /**
   * Should this ACTION be offered? The button-level counterpart to isVisible.
   *
   * Same bridge, same reason: the RBAC grid decides for a registered module,
   * the legacy role list decides for everything else. Without this, granting a
   * role an operation made the API accept it while the button stayed hidden —
   * which is how `studies.create` ended up allowed for Platform Admin with no
   * way to press it.
   *
   * `legacyRoles` is the old CAN_* constant. Pass it so an unmigrated module
   * keeps behaving exactly as today; it is ignored once the module is
   * registered, which is what makes the grid authoritative.
   */
  const canDo = (module: string, verb: string, legacyRoles?: readonly string[]): boolean => {
    const role = user?.role;
    if (!role) return false;
    const legacy = () => (legacyRoles ? legacyRoles.includes(role) : false);
    // Not loaded yet, or the call failed: behave exactly as before.
    if (!data) return legacy();
    if (registered.has(module)) return verbsFor(module).includes(verb);
    return legacy();
  };

  /**
   * Does the user hold THIS EXACT operation? The check an action must use.
   *
   * `canDo` asks the module, whose verb list is a union across the operations
   * in a cell. (studies, create) holds studies.create,
   * studies.ie_checklist.create and studies.schedule.visit.create — so a role
   * with the last two reports `create` on the module even when studies.create
   * denies them. Gating the New-study button on that opened a wizard the API
   * then refused.
   *
   * `operationKey` is the backend's own key. `legacyRoles` covers the window
   * before /me/permissions answers, and any module not yet migrated.
   */
  const holdsOperation = (operationKey: string, legacyRoles?: readonly string[]): boolean => {
    const role = user?.role;
    if (!role) return false;
    const legacy = () => (legacyRoles ? legacyRoles.includes(role) : false);
    if (!data) return legacy();
    const module = operationKey.split('.')[0] ?? '';
    if (registered.has(module)) return (data.operations ?? []).includes(operationKey);
    return legacy();
  };

  /**
   * Is this nav entry / page visible?
   *
   * `module` is the RBAC module; `page` is the legacy PageKey. Both are needed
   * during the migration — the first answers for registered modules, the
   * second for everything else.
   */
  const isVisible = (page: PageKey): boolean => {
    const role = user?.role;
    if (!role) return false;
    // Not loaded yet, or the call failed: behave exactly as before.
    if (!data) return canSeePage(role, page);

    // ACTION pages are gated on the exact operation, not on the module.
    // "Show the module if they can do anything in it" is the right rule for
    // the sidebar; applied to /studies/new it let a role open a wizard whose
    // submit the API refuses.
    const action = PAGE_TO_OPERATION[page];
    if (action) {
      const module = action.split('.')[0] ?? '';
      if (registered.has(module)) return (data.operations ?? []).includes(action);
      return canSeePage(role, page);
    }

    const module = PAGE_TO_MODULE[page];
    if (module && registered.has(module)) return verbsFor(module).length > 0;
    return canSeePage(role, page);
  };

  return {
    isLoading: q.isLoading,
    /** True once the grid is authoritative for at least one module. */
    ready: !!data,
    modules: data?.modules ?? {},
    registeredModules: registered,
    verbsFor,
    can,
    canDo,
    holdsOperation,
    isVisible,
  };
}
